/**
 * Profile self-edit page. Any user can update:
 *   - Nama Lengkap, Username, Tanggal Lahir, NIK, Email
 * Password change: self-serve — user provides current + new password, system updates
 * the hash AND emails the new credentials to the admin inbox.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Save, User, KeyRound, AlertTriangle, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { API } from "../../lib/api";

function PwField({ label, value, onChange, show, toggleShow, testid }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ position: "relative" }}>
        <input className="select-input" type={show ? "text" : "password"}
          value={value} onChange={onChange} data-testid={testid}
          autoComplete="off" style={{ paddingRight: 38 }} />
        <button type="button" onClick={toggleShow}
          style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer", color: "var(--muted)",
                    padding: 6 }}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

export default function ProfileView({ currentUser, onUserUpdated }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", new_username: "", birthdate: "", employee_id: "", email: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwStatus, setPwStatus] = useState(null);

  const loadMe = async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setMe(r.data);
      setForm({
        name: r.data.name || "", new_username: r.data.username || "",
        birthdate: r.data.birthdate || "", employee_id: r.data.employee_id || "",
        email: r.data.email || "",
      });
    } catch { /* noop */ }
  };
  useEffect(() => { loadMe(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name, birthdate: form.birthdate || null,
        employee_id: form.employee_id || null, email: form.email || null,
      };
      if (form.new_username && form.new_username !== me.username) payload.new_username = form.new_username;
      const r = await axios.patch(`${API}/auth/me/profile`, payload);
      setToast("✅ Profil tersimpan");
      setMe(r.data.user);
      onUserUpdated?.(r.data.user);
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      setToast(`❌ ${e?.response?.data?.detail || "Gagal simpan"}`);
    } finally { setBusy(false); }
  };

  const changePw = async () => {
    setPwBusy(true); setPwStatus(null);
    if (pw.next !== pw.confirm) {
      setPwStatus({ ok: false, msg: "Password baru dan konfirmasi tidak sama." });
      setPwBusy(false); return;
    }
    if (pw.next.length < 8) {
      setPwStatus({ ok: false, msg: "Password baru minimal 8 karakter." });
      setPwBusy(false); return;
    }
    try {
      const r = await axios.post(`${API}/auth/me/change-password`, {
        current_password: pw.current, new_password: pw.next,
      });
      setPwStatus({ ok: true, msg: r.data?.message || "✅ Password berhasil diganti. Notifikasi sudah dikirim ke admin." });
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwStatus({ ok: false, msg: e?.response?.data?.detail || "Gagal mengganti password." });
    } finally { setPwBusy(false); }
  };

  if (!me) return <div className="empty">Memuat profil...</div>;

  return (
    <div data-testid="profile-view" style={{ maxWidth: 820, margin: "0 auto" }}>
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <User size={14} color="var(--accent)" /> Profil Saya
      </div>

      {toast && <div className="alert ok" style={{ margin: "8px 0" }} data-testid="profile-toast">{toast}</div>}

      <div className="card card-3d" style={{ marginTop: 12 }}>
        <div className="card-h" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 50, height: 50, borderRadius: "50%", background: "var(--accent)",
                         color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                         fontWeight: 700, fontSize: 18 }}>
            {(me.name || me.username || "U").trim().slice(0, 2).toUpperCase()}
          </div>
          <div>
            <strong style={{ fontSize: 16 }}>{me.name || me.username}</strong>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>
              @{me.username} · {me.role_label || me.role}{me.team ? " · Team " + me.team : ""}
            </div>
          </div>
        </div>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div className="field"><label>Nama Lengkap</label>
            <input className="select-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </div>
          <div className="field"><label>Username</label>
            <input className="select-input" value={form.new_username}
              onChange={(e) => setForm({ ...form, new_username: e.target.value })} data-testid="profile-username" />
          </div>
          <div className="field"><label>Tanggal Lahir</label>
            <input className="select-input" type="date" value={form.birthdate || ""}
              onChange={(e) => setForm({ ...form, birthdate: e.target.value })} data-testid="profile-birthdate" />
          </div>
          <div className="field"><label>Nomor Induk Karyawan</label>
            <input className="select-input" value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="profile-empid" />
          </div>
          <div className="field" style={{ gridColumn: "1 / -1" }}><label>Email</label>
            <input className="select-input" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
          </div>
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="profile-save">
              <Save size={14} /> Simpan Perubahan
            </button>
          </div>
        </div>
      </div>

      <div className="card card-3d" style={{ marginTop: 16 }}>
        <div className="card-h">
          <KeyRound size={14} color="var(--warn)" />
          <strong style={{ marginLeft: 8 }}>Ganti Password</strong>
          <span className="badge" style={{ marginLeft: "auto", background: "rgba(39,174,96,.15)", color: "var(--good)" }}>
            <ShieldCheck size={10} /> Self-Service
          </span>
        </div>
        <div className="card-b">
          <div className="alert" style={{ background: "rgba(46,134,222,.08)", border: "1px solid #B8D4F2",
                                            color: "#1E5AA8", marginBottom: 14, padding: 12, fontSize: 12 }}>
            <AlertTriangle size={12} /> Anda dapat mengganti password kapan saja. Setiap perubahan akan otomatis
            dikirim ke email admin sebagai notifikasi audit.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <PwField label="Password Saat Ini *" value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              show={showPw.current} toggleShow={() => setShowPw({ ...showPw, current: !showPw.current })}
              testid="pw-current" />
            <div /> {/* spacer */}
            <PwField label="Password Baru * (min 8 char)" value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              show={showPw.next} toggleShow={() => setShowPw({ ...showPw, next: !showPw.next })}
              testid="pw-new" />
            <PwField label="Konfirmasi Password Baru *" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              show={showPw.confirm} toggleShow={() => setShowPw({ ...showPw, confirm: !showPw.confirm })}
              testid="pw-confirm" />
          </div>
          {pwStatus && (
            <div className={`alert ${pwStatus.ok ? "ok" : "err"}`} style={{ marginTop: 12 }} data-testid="pw-status">
              {pwStatus.msg}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 8 }}>
            <button className="btn btn-primary" onClick={changePw}
              disabled={pwBusy || !pw.current || !pw.next || !pw.confirm}
              data-testid="pw-submit" style={{ background: "var(--good)" }}>
              <Mail size={14} /> {pwBusy ? "Memproses…" : "Ganti Password & Notifikasi Admin"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
